import type { ReactNode } from 'react';
import type { ConceptAnalysisArtifactSummary, CreationArtifactEdgeV1, CreationArtifactUsageV1, CreationArtifactV1, CreationFilmMaterialV1, CreationFilmRunV1, GameDesign, ReferenceBrief, VideoBrief } from '@djgg/creations';
import { plainPlanText } from '@djgg/creations';
import { isSuperadmin } from '../../auth/model.js';
import { useAuth } from '../../services/context.js';
import { CREATION_GUIDES, type CreationGuideStage } from '../../components/CreationGuide.js';
import { analysisDiscoveries } from '../creationJourney.js';
import { CreationShow } from '../CreationShow.js';
import { appendFilmMaterialArtifacts, filmLastUpdatedAt } from '../creationFilmModel.js';
import { shortCopy, type CreationRunMoment, type ShowArtifact, type ShowChapter } from '../creationShowModel.js';
import { creationArtifactShowArtifacts } from '../creationArtifactModel.js';

/** Adapter only: Create and Iterate use the same presentation engine. */
export function WizardStoryReel(p: {
  stage: CreationGuideStage; running?: boolean; concept: string; referenceUrls: readonly string[];
  referenceBriefs?: readonly ReferenceBrief[]; videoBriefs?: readonly VideoBrief[];
  design?: GameDesign | null; completedArtifacts?: readonly ConceptAnalysisArtifactSummary[];
  identity?: string; title?: string; activity?: string; chapter?: ShowChapter; details?: ReactNode; actions?: ReactNode; lastUpdatedAt?: number; scopeChanges?: readonly string[];
  filmEnabled?: boolean; filmRun?: CreationFilmRunV1 | null; filmMaterials?: readonly CreationFilmMaterialV1[];
  creationArtifacts?: readonly CreationArtifactV1[];
  creationArtifactEdges?: readonly CreationArtifactEdgeV1[];
  creationArtifactUsages?: readonly CreationArtifactUsageV1[];
  creationRunId?: string | null;
  onCreationRunMoment?: (runId: string, moment: CreationRunMoment) => void;
  activeVersion?: number;
  /** Questions offered inside the film: it pauses behind them and plays on after. */
  choice?: ReactNode; choiceKey?: string;
  /** "Keep making my game" on the questions, beyond hiding them. */
  onChoiceSkip?: () => void;
  ready?: boolean; playUrl?: string; notInYetLine?: string;
  onCancel?: () => void; cancelling?: boolean;
}) {
  const { user } = useAuth();
  const guide = CREATION_GUIDES[p.stage];
  const discoveries = analysisDiscoveries(p);
  // Old stored mechanical/vocabulary rows remain in the saved design. They
  // are summarized here rather than replayed as dozens of generic slides.
  const saved = (p.completedArtifacts ?? []).filter(item => item.kind !== 'gameplay-idea' && item.kind !== 'vocabulary');
  const merged = [...new Map([...saved, ...discoveries].map(item => [item.id, item])).values()];
  const artifacts: ShowArtifact[] = creationArtifactShowArtifacts(
    p.creationArtifacts ?? [], p.title ?? '', p.creationArtifactEdges, p.creationArtifactUsages,
  );
  const discoveryArtifacts: ShowArtifact[] = merged.map(item => {
    const media = 'media' in item ? item.media : undefined;
    const shared = { id: item.id, title: shortCopy(item.title, 65), summary: item.summary,
      unavailable: item.state === 'unavailable' };
    if (media && item.thumbnailUrl) return { ...shared, kind: 'inspiration', url: item.thumbnailUrl,
      origin: 'reference', intrinsicWidth: media.intrinsicWidth, intrinsicHeight: media.intrinsicHeight,
      label: 'Inspiration' };
    return { ...shared, kind: item.thumbnailUrl ? 'image' : 'discovery', url: item.thumbnailUrl,
      label: item.state === 'preparing' ? 'Exploring' : 'Discovery' };
  });
  if (p.stage !== 'idea') artifacts.push(...discoveryArtifacts);
  if (p.scopeChanges?.length && isSuperadmin(user)) artifacts.push({ id: 'scope-adaptation', title: 'Adjusted for this version',
    summary: `Not included yet: ${p.scopeChanges.slice(0, 2).map(plainPlanText).join('; ')}. Full scope is in details.`, kind: 'discovery', label: 'Your game plan' });
  if (p.stage !== 'idea') appendFilmMaterialArtifacts(artifacts, p.filmMaterials ?? []);
  return <div data-testid="wizard-story-reel"><CreationShow identity={p.identity ?? `idea:${p.concept}`}
    creationRunId={p.creationRunId} onCreationRunMoment={p.onCreationRunMoment}
    title={p.title ?? 'Your next game'} chapter={p.chapter ?? (p.stage === 'approval' ? 'Plan' : 'Discover')}
    activity={p.activity ?? guide.title} artifacts={artifacts} running={p.running || p.stage === 'analysis'}
    filmEnabled={p.filmEnabled} filmRunSeed={p.filmRun ? String(p.filmRun.seed) : undefined}
    choice={p.choice} choiceKey={p.choiceKey} choiceVisible={Boolean(p.choice)} onChoiceSkip={p.onChoiceSkip}
    ready={p.ready} playUrl={p.playUrl} notInYetLine={p.notInYetLine} onCancel={p.onCancel} cancelling={p.cancelling}
    activeVersion={p.activeVersion ?? 0}
    lastUpdatedAt={filmLastUpdatedAt(p.lastUpdatedAt, p.filmRun)} failed={p.stage === 'failed'} mascot={guide.art} details={p.details} actions={p.actions} /></div>;
}
